/**
 * WhatsApp no Uso & Custos (0.1.87): mensagens do mês por condomínio e custo estimado.
 * O custo real vem na fatura da Meta (cartão cadastrado no Gerenciador do WhatsApp);
 * aqui é a estimativa (preço por mensagem × mensagens cobradas), para acompanhar.
 */
import { useQuery } from "@tanstack/react-query";
import { MessageCircle } from "lucide-react";
import { api } from "../lib/api";

interface Item {
  condominio_id: number; nome: string; enviadas: number; entregues: number;
  lidas: number; falhas: number; custo_brl: number;
}
interface Resumo {
  ano: number; mes: number; itens: Item[]; total_enviadas: number;
  total_custo_brl: number; preco_brl: number; habilitado: boolean;
}

const brl = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export function WhatsappUsoCard({ ano, mes }: { ano: number; mes: number }) {
  const { data } = useQuery<Resumo>({
    queryKey: ["uso", "whatsapp", ano, mes],
    queryFn: () => api.get<Resumo>(`/uso/whatsapp?ano=${ano}&mes=${mes}`),
  });
  if (!data) return null;
  if (!data.habilitado && data.itens.length === 0) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 mb-6">
      <h3 className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-1.5">
        <MessageCircle size={16} className="text-green-700" /> WhatsApp — avisos aos moradores
      </h3>
      <p className="text-[11px] text-slate-500 mb-3">
        Só recebe quem autorizou no Portal. Custo <b>estimado</b> de {brl(data.preco_brl)} por
        mensagem cobrada; o valor oficial vem na fatura da Meta.
      </p>
      {data.itens.length === 0 ? (
        <p className="text-sm text-slate-400">Nenhuma mensagem neste mês.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase text-slate-500">
            <tr>
              <th className="text-left font-semibold py-1">Condomínio</th>
              <th className="text-right font-semibold py-1">Enviadas</th>
              <th className="text-right font-semibold py-1">Lidas</th>
              <th className="text-right font-semibold py-1">Falhas</th>
              <th className="text-right font-semibold py-1">Custo (R$)</th>
            </tr>
          </thead>
          <tbody>
            {data.itens.map((i) => (
              <tr key={i.condominio_id} className="border-t border-slate-100">
                <td className="py-1.5 text-slate-800">{i.nome}</td>
                <td className="py-1.5 text-right tabular-nums">{i.enviadas}</td>
                <td className="py-1.5 text-right tabular-nums">{i.lidas}</td>
                <td className={`py-1.5 text-right tabular-nums ${i.falhas ? "text-rose-600" : ""}`}>{i.falhas}</td>
                <td className="py-1.5 text-right tabular-nums">{brl(i.custo_brl)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-slate-200 font-semibold">
              <td className="py-1.5">Total</td>
              <td className="py-1.5 text-right tabular-nums">{data.total_enviadas}</td>
              <td colSpan={2} />
              <td className="py-1.5 text-right tabular-nums">{brl(data.total_custo_brl)}</td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}
